"use client";

import { useRef, useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
} from "lucide-react";
import video1 from "../assets/videos/video1.mp4";
import video2 from "../assets/videos/video2.mp4";
import video3 from "../assets/videos/video3.mp4";
import video4 from "../assets/videos/video4.mp4";

export default function VideoSection() {
  useEffect(() => {
    AOS.init({
      duration: 800, // animation duration
      easing: "ease-in-out",
      once: false, // allows animation to happen more than once
      mirror: true, // animate out when scrolling past and animate back when scrolling back
    });
  }, []);

  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [mutedVideos, setMutedVideos] = useState(new Set());

  const videos = [video1, video2, video3, video4, video1];

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      checkScrollPosition();
    }
    return () =>
      scrollRef.current?.removeEventListener("scroll", checkScrollPosition);
  }, []);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  const togglePlay = (index, videoElement) => {
    if (playingVideo === index) {
      videoElement.pause();
      setPlayingVideo(null);
    } else {
      videoElement.play();
      setPlayingVideo(index);
    }
  };

  const toggleMute = (index, videoElement) => {
    const newMutedVideos = new Set(mutedVideos);
    if (mutedVideos.has(index)) {
      newMutedVideos.delete(index);
      videoElement.muted = false;
    } else {
      newMutedVideos.add(index);
      videoElement.muted = true;
    }
    setMutedVideos(newMutedVideos);
  };

  const enterFullscreen = (videoElement) => {
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    }
  };

  return (
    <section className="bg-light py-4 container-1089">
      <div className="position-relative  video-strip">
        {/* Scrollable container */}
        <div
          ref={scrollRef}
          className="d-flex overflow-auto pb-3 video-scroll" // 👈 add ps-5 pe-5
          style={{ scrollBehavior: "smooth" }}
        >
          {videos.map((src, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 me-3 position-relative"
              data-aos="fade-up"
              data-aos-delay={idx * 100}
            >
              <video
                src={src}
                autoPlay
                muted
                loop
                playsInline
                className="rounded shadow"
                style={{
                  width: "280px",
                  height: "500px",
                  objectFit: "cover",
                  minWidth: "200px",
                }}
                onClick={(e) => {
                  const video = e.currentTarget;
                  video.currentTime = 0; // restart
                  video.muted = false; // enable sound
                  video.play();
                }}
              />

              {/* Overlay controls */}
              <div
                className="position-absolute rounded top-0 start-0 w-100 d-flex justify-content-center align-items-center"
                style={{
                  backgroundColor: "rgba(0,0,0,0.3)",
                  opacity: 0,
                  transition: "opacity 0.3s",
                  height: "500px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              >
                <div className="d-flex gap-2">
                  {/* Play/Pause */}
                  <button
                    className="btn btn-light btn-sm rounded-circle"
                    style={{ width: "50px", height: "50px" }}
                    onClick={(e) => {
                      const video = e.currentTarget
                        .closest(".flex-shrink-0")
                        ?.querySelector("video");
                      if (video) togglePlay(idx, video);
                    }}
                  >
                    {playingVideo === idx ? <Pause /> : <Play />}
                  </button>

                  {/* Mute/Unmute */}
                  <button
                    className="btn btn-light btn-sm rounded-circle"
                    style={{ width: "50px", height: "50px" }}
                    onClick={(e) => {
                      const video = e.currentTarget
                        .closest(".flex-shrink-0")
                        ?.querySelector("video");
                      if (video) toggleMute(idx, video);
                    }}
                  >
                    {mutedVideos.has(idx) ? <VolumeX /> : <Volume2 />}
                  </button>

                  {/* Fullscreen */}
                  <button
                    className="btn btn-light btn-sm rounded-circle"
                    style={{ width: "50px", height: "50px" }}
                    onClick={(e) => {
                      const video = e.currentTarget
                        .closest(".flex-shrink-0")
                        ?.querySelector("video");
                      if (video) enterFullscreen(video);
                    }}
                  >
                    <Maximize />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showLeftArrow && (
          <button
            className="video-arrow video-arrow start-0 translate-middle-y"
            onClick={scrollLeft}
          >
            <ChevronLeft />
          </button>
        )}

        {showRightArrow && (
          <button
            className="video-arrow video-arrow end-0 translate-middle-y"
            onClick={scrollRight}
          >
            <ChevronRight />
          </button>
        )}
      </div>
    </section>
  );
}
